import React, {useState, useRef, useEffect} from "react";
import {NavLink} from "react-router-dom";
import {ChevronDown, ChevronUp} from "lucide-react";
import {LucideIcon} from "lucide-react";

interface NavigationItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

interface EnvDropdownProps {
  sections: {
    header: string;
    items: NavigationItem[];
  }[];
  currentPath: string;
  onNavigate: () => void;
}

const EnvDropdown: React.FC<EnvDropdownProps> = ({
  sections,
  currentPath,
  onNavigate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find current page info
  const getCurrentPageInfo = () => {
    for (const section of sections) {
      for (const item of section.items) {
        if (currentPath === item.path) {
          return {section: section.header, item};
        }
      }
    }
    return null;
  };

  const currentPage = getCurrentPageInfo();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = () => {
    setIsOpen(false);
    onNavigate();
  };

  return (
    <div className="env-dropdown-container" ref={dropdownRef}>
      <button
        className="env-dropdown-toggle"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select environment page"
      >
        <div className="env-dropdown-current">
          {currentPage ? (
            <>
              <currentPage.item.icon size={18} className="env-dropdown-icon" />
              <span className="env-dropdown-label">
                {currentPage.item.label}
              </span>
            </>
          ) : (
            <span className="env-dropdown-label">Select Page</span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp size={16} className="env-dropdown-chevron" />
        ) : (
          <ChevronDown size={16} className="env-dropdown-chevron" />
        )}
      </button>

      {isOpen && (
        <div className="env-dropdown-menu" role="listbox">
          {sections.map((section, sectionIndex) => (
            <div key={section.header} className="env-dropdown-section">
              {sectionIndex > 0 && <hr className="env-dropdown-divider" />}
              <div className="env-dropdown-section-header">
                {section.header}
              </div>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({isActive}) =>
                    `env-dropdown-item ${isActive ? "env-dropdown-item-active" : ""}`
                  }
                  onClick={handleItemClick}
                  role="option"
                  aria-selected={currentPath === item.path}
                >
                  <item.icon size={18} className="env-dropdown-item-icon" />
                  <span className="env-dropdown-item-label">{item.label}</span>
                  {currentPath === item.path && (
                    <div className="env-dropdown-item-check">✓</div>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnvDropdown;
